import { Check } from "lucide-react";
import { useRef, type ComponentType, type ReactElement, type SVGAttributes } from "react";
import { Dropdown, ListType, PopupMenu } from "./ring.ts";

export interface MenuItem {
  key: string;
  label: string;
  /** A lucide icon component works: Ring renders any SVG component as a glyph. */
  icon?: ComponentType<SVGAttributes<SVGSVGElement>>;
  /** Tick on the left (YouTrack's settings menus); unticked siblings keep the column so labels align. */
  checked?: boolean;
  /** A separator line above this item. */
  separator?: boolean;
  onClick: () => void;
}

const Blank = (p: SVGAttributes<SVGSVGElement>) => <svg width={16} height={16} {...p} />;

// Menus that are currently open. Ring's own Esc handling skips key events coming from inputs and
// contenteditables, so hosts like the Markdown editor close menus through closeMenus() instead.
const openMenus = new Set<() => void>();

/** Close every open Menu; true if there was one (the caller then treats its Esc as handled). */
export function closeMenus(): boolean {
  if (openMenus.size === 0) return false;
  for (const close of [...openMenus]) close();
  return true;
}

/** Click-to-open action menu (Ring Dropdown + PopupMenu): the anchor gets the click handler, the list closes on select, Esc or outside click. */
/** `keepOpen`: the list stays up after a click, for toggling several checked items in a row (the column picker). */
export function Menu({ anchor, items, title, keepOpen = false, onToggle }: { anchor: ReactElement; items: MenuItem[]; title?: string; keepOpen?: boolean; onToggle?: (open: boolean) => void }) {
  const dropdown = useRef<Dropdown>(null);
  const close = useRef(() => dropdown.current?.toggle(false));
  const anyChecked = items.some((i) => i.checked !== undefined);
  const data = [
    ...(title ? [{ rgItemType: ListType.TITLE, key: "__title", label: title }] : []),
    ...items.flatMap((i) => [
      ...(i.separator ? [{ rgItemType: ListType.SEPARATOR, key: `__sep_${i.key}` }] : []),
      { key: i.key, label: i.label, glyph: i.checked ? Check : anyChecked ? Blank : i.icon },
    ]),
  ];
  return (
    <Dropdown
      ref={dropdown}
      anchor={anchor}
      onShow={() => {
        openMenus.add(close.current);
        onToggle?.(true);
      }}
      onHide={() => {
        openMenus.delete(close.current);
        onToggle?.(false);
      }}
    >
      <PopupMenu closeOnSelect={!keepOpen} data={data} onSelect={(item) => items.find((i) => i.key === item.key)?.onClick()} />
    </Dropdown>
  );
}
